<script lang="ts" setup>
const props = withDefaults(defineProps<{ data: AllReview, hasParent?: boolean, isOpen?: boolean, level?: number, isModal?: boolean, businessName?: string }>(), {
  level: 1,
})
const emit = defineEmits<{
  (e: 'updated'): void
}>()
const { userId, user, isUsers, admin } = useUser()
const { openModal } = useGlobalModal(() => {
  emit('updated')
})
const shareVisible = ref(false)
function openShare() {
  shareVisible.value = true
}
const router = useRouter()
function unHighlight() {
  router.replace({ query: { ...route.query, review: undefined } })
}

const route = useRoute()
const isInRouteParams = computed(() => route?.query?.review && route.query.review === props.data.id)

watch(isInRouteParams, (val) => {
  if (val && !props.hasParent) {
    openModal({
      title: 'Review',
      description: props.data.review,
      confirm: () => {},
      cancel: () => unHighlight(),
      component: 'reviewCard',
      componentProps: {
        data: props.data,
        hasParent: props.hasParent,
        isModal: true,
        isOpen: true,
        level: props.level + 1,
        businessName: props.businessName,
      },
    })
    useSeoMeta({
      title: props.isModal ? `Review for ${props.businessName ?? ''}` : undefined,
      ogTitle: props.isModal ? `Review for ${props.businessName ?? ''}` : undefined,
      description: props.isModal ? props.data.review : undefined,
      ogDescription: props.isModal ? props.data.review : undefined,
      twitterTitle: props.isModal ? `Review for ${props.businessName ?? ''}` : undefined,
      twitterDescription: props.isModal ? props.data.review : undefined,
      // ogImage: 'https://example.com/image.png',
      // twitterCard: 'summary_large_image',
    })

    defineOgImageComponent('Review', {
      title: `Review`,
      backgroundColor: 'bg-slate-100',
      review: props.data.review,
      businessName: props.businessName,
    })
  }
}, { immediate: true })

const replyOpen = ref(false)

function onReplySaved() {
  emit('updated')
  replyOpen.value = false
}

function openCard() {
  router.replace({ query: { ...route.query, review: props.data.id } })
}

const isUsersItem = computed(() => {
  if (admin.value) return true
  const col = GetCollection(props.data.reviewedEntity)
  if (!col?.collection || !props.data.reviewedId) return false
  return isUsers(props.data.reviewedId, col.collection)
})
</script>

<template>
  <div class="flex flex-col gap-3">
    <UICard class="overflow-clip relative text-blue font-semibold">
      <UModal
        v-model="shareVisible"
        :ui="{ background: 'bg-pink' }"
      >
        <div class="p-6 md:p-8 lg:p-12 flex flex-col gap-3">
          <HeadingText
            tag="h4"
            class="text-white"
          >
            Share this review with your network
          </HeadingText>
          <SocialShare
            facebook
            twitter
            linked-in
            direct-link
            :added-url="`?review=${data.id}`"
            :review-id="data.id"
          />
        </div>
      </UModal>
      <div class="flex justify-between gap-3">
        <UIcon
          v-if="!hasParent"
          name="ri:double-quotes-l"
          class="w-10 h-10 text-yellow"
        />
        <CollectionReviewHide
          v-if="isUsersItem"
          :id="data.id"
          :visible="data.visible"
          class="self-end"
        />
      </div>
      <div class="relative flex flex-col h-full pt-2 justify-center">
        <CollectionBusinessRating
          v-if="data.stars && !hasParent"
          :rating="data.stars"
          class="text-yellow ml-auto"
        />
        <p class="md:text-lg lg:text-xl pt-3 flex">
          {{ data.review }}
        </p>

        <CollectionReviewGallery
          :id="data.id"
          :gallery="data.images"
          class="w-full mt-6"
        />

        <div class="flex flex-wrap gap-6 justify-between items-center pt-6 ">
          <div class="gap-3 flex flex-wrap items-center">
            <UTooltip
              v-if="!hasParent"
              text="Share"
              @click="openShare"
            >
              <UButton
                variant="soft"
                rounded
                icon="ri:share-line"
              />
            </UTooltip>
            <UButton
              v-if="!isOpen"
              @click="openCard"
            >
              {{ data?.replyCount ?? 0 }} {{ data?.replyCount === 1 ? 'Reply' : 'Replies' }}
            </UButton>
            <UTooltip
              v-if="userId && !hasParent"
              text="Reply"
              @click="replyOpen = !replyOpen"
            >
              <UButton
                variant="ghost"
                rounded
                icon="ri:reply-line"
              />
            </UTooltip>
          </div>

          <div class="flex justify-end gap-6 items-center">
            <div class="flex flex-col">
              <p>
                {{ data.reviewer === userId ? user?.name : data.reviewer }}
              </p>
            </div>
            <div
              class=" rounded-full overflow-clip flex items-center justify-center shadow-lg bg-blue"
              :class="[hasParent ? 'w-8 h-8' :'w-10 h-10 md:h-12 md:w-12 lg:w-16 lg:h-16  ']"
            >
              <UIcon

                name="heroicons:user-20-solid"
                class=" text-white bg-white"
                :class="[hasParent ? 'w-6 h-6' : 'w-10 h-10 md:h-12 md:w-12 lg:w-16 lg:h-16']"
              />
            </div>
          </div>
        </div>
      </div>
      <transition
        name="fade"
        mode="out-in"
      >
        <CollectionReviewReply
          v-if="replyOpen"
          class="mt-3"
          :parent-id="data.id"
          @created="onReplySaved()"
        />
      </transition>
    </UICard>
    <div
      v-if="data?.children?.length && isOpen"
      class="gap-3 flex flex-col pl-6"
    >
      <CollectionReviewCardBasic
        v-for="reply in data.children"
        :key="reply.id"
        :data="reply"
        :is-users-item="isUsersItem"
        :is-open="true"
      />
    </div>
  </div>
</template>
